import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { formatDay, type OrderListItem, RACK_TYPE_SHORT, type StaffMember, type Unit, UNIT_STATE_LABEL } from '@reikraft/shared';
import { cx, Empty, Icon, Loader, Modal, SearchBox, StageBar, StatusBadge } from '@reikraft/ui';
import { api, ApiError, qs } from '../api';
import { useDebounced } from '../hooks';
import { formatMoney, partyKind } from '../format';

export function PageHead({
  title,
  sub,
  crumbs,
  actions,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  crumbs?: { label: string; to?: string }[];
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      {crumbs && (
        <nav aria-label="Хлебные крошки" className="rk-crumbs">
          {crumbs.map((c, i) => (
            <span key={c.label}>
              {i > 0 && ' / '}
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <div className="adm-head">
        <div className="adm-head__title">
          {typeof title === 'string' ? <h1 className="rk-h1">{title}</h1> : title}
          {sub && <div className="adm-head__sub">{sub}</div>}
        </div>
        {children}
        {actions && (
          <div className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
            {actions}
          </div>
        )}
      </div>
    </>
  );
}

/** Ошибки полей из ответа API */
export function useFormErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  return {
    errors,
    message,
    set(e: unknown) {
      if (e instanceof ApiError) {
        setErrors(e.fields);
        setMessage(Object.keys(e.fields).length ? null : e.message);
      } else {
        setMessage(e instanceof Error ? e.message : 'Ошибка');
      }
    },
    clear() {
      setErrors({});
      setMessage(null);
    },
  };
}

export function FormMessage({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="rk-notice rk-notice--danger" role="alert">
      {message}
    </div>
  );
}

/** Меню действий: кнопка «…» со списком */
export function Menu({ label, children, button }: { label: string; children: ReactNode; button?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="adm-menu" ref={ref}>
      <button
        type="button"
        className="rk-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {button ?? <Icon name="more" />}
      </button>
      {open && (
        <div className="adm-menu__list" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

export function useStaff() {
  return useQuery({ queryKey: ['staff'], queryFn: () => api.get<StaffMember[]>('/admin/staff'), staleTime: 60_000 });
}

export function MasterSelect({ value, onChange, id }: { value: string | null; onChange: (v: string | null) => void; id?: string }) {
  const staff = useStaff();
  const list = (staff.data ?? []).filter((s) => s.isActive && (s.role === 'master' || s.role === 'admin' || s.role === 'manager'));
  return (
    <select id={id} className="rk-select" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— не назначен</option>
      {list.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

/** Карточки заказов для телефона */
export function OrderCards({ items, showMaster }: { items: OrderListItem[]; showMaster?: boolean }) {
  if (!items.length) return <Empty>Заказов нет</Empty>;
  return (
    <div className="adm-cards">
      {items.map((o) => (
        <Link key={o.id} to={`/orders/${o.id}`} className="adm-card">
          <span className="adm-card__top">
            <b className="rk-mono">{o.number}</b>
            <StatusBadge status={o.status} kind={o.kind} />
          </span>
          <span>
            {o.car ?? 'Авто не указано'} <span className="rk-muted rk-mono">· {o.rackType ? RACK_TYPE_SHORT[o.rackType] : '—'}</span>
          </span>
          <span className="adm-card__sub">
            <span>
              {o.party?.name ?? 'Пополнение фонда'}
              {showMaster && o.masterName ? ` · ${o.masterName}` : ''}
            </span>
            <span className="rk-mono" style={{ color: o.overdue ? 'var(--danger-text)' : undefined }}>
              {o.dueDate ? formatDay(o.dueDate) : ''}
              {o.overdue ? ' · ПРОСРОЧЕН' : ''}
            </span>
          </span>
          <StageBar kind={o.kind} status={o.status} fluid />
        </Link>
      ))}
    </div>
  );
}

export function OrderTableRows({ items }: { items: OrderListItem[] }) {
  return (
    <div className="adm-cards">
      {items.map((o) => (
        <Link key={o.id} to={`/orders/${o.id}`} className="adm-card">
          <span className="adm-card__top">
            <b className="rk-mono">{o.number}</b>
            <StatusBadge status={o.status} kind={o.kind} />
          </span>
          <span className="adm-card__sub">
            <span>
              {o.car ?? '—'} · {partyKind(o.party)}
            </span>
            <span className="rk-mono">{formatMoney(o.totalKop)}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

/** Выбор готовой рейки из фонда */
export function UnitPicker({
  open,
  onClose,
  onPick,
  title = 'Рейка из обменного фонда',
  initialQuery = '',
}: {
  open: boolean;
  onClose: () => void;
  onPick: (u: Unit) => void;
  title?: string;
  initialQuery?: string;
}) {
  const [q, setQ] = useState(initialQuery);
  useEffect(() => {
    if (open) setQ(initialQuery);
  }, [open, initialQuery]);
  const dq = useDebounced(q);
  const list = useQuery({
    queryKey: ['units', 'available', dq],
    queryFn: () => api.get<Unit[]>(`/admin/stock/units${qs({ available: 1, q: dq })}`),
    enabled: open,
  });
  const items = (list.data ?? []).filter((u) => !u.reservation);
  const reserved = (list.data ?? []).filter((u) => u.reservation);
  return (
    <Modal open={open} onClose={onClose} title={title} wide>
      <SearchBox value={q} onChange={setQ} placeholder="Модель, артикул, OEM, бирка" label="Поиск по фонду" />
      {list.isLoading ? (
        <Loader />
      ) : (
        <div className="adm-cards" style={{ maxHeight: 420, overflowY: 'auto' }}>
          {items.map((u) => (
            <button
              key={u.id}
              type="button"
              className="adm-card"
              style={{ background: 'transparent', border: 0, borderBottom: '1px solid var(--line)', textAlign: 'left' }}
              onClick={() => onPick(u)}
            >
              <span className="adm-card__top">
                <b className="rk-mono">{u.productSku ?? 'без позиции'}</b>
                <span className="rk-mono rk-muted">бирка {u.code}</span>
              </span>
              <span className="adm-card__sub">
                <span>
                  {u.rackType ? RACK_TYPE_SHORT[u.rackType] : '—'} · {UNIT_STATE_LABEL[u.state]}
                  {u.oem ? ` · ${u.oem}` : ''}
                </span>
                <span className="rk-mono">{u.place ?? '—'}</span>
              </span>
            </button>
          ))}
          {!items.length && <Empty>Свободных реек не найдено</Empty>}
        </div>
      )}
      {reserved.length > 0 && <div className="rk-muted">Ещё {reserved.length} в резерве — их выдать нельзя, пока резерв не снят.</div>}
    </Modal>
  );
}

export function Truncate({ children, max = 60 }: { children: string | null | undefined; max?: number }) {
  if (!children) return <>—</>;
  return <span title={children}>{children.length > max ? `${children.slice(0, max)}…` : children}</span>;
}

export function Hint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cx('rk-muted', className)} style={{ margin: 0, fontSize: 13 }}>
      {children}
    </p>
  );
}
